import * as React from "react"
import { VercelIntegrationWizard } from "@modules/cdn/vercel"
import { NetlifyIntegrationWizard } from "@modules/cdn/netlify"
import { FastlyIntegrationForm } from "@modules/cdn/fastly"
import { AmazonS3IntegrationForm } from "@modules/cdn/amazonS3"
import { AzureIntegrationForm } from "@modules/cdn/azure"
import { GCloudStorageIntegrationForm } from "@modules/cdn/gcloudStorage"
import { FirebaseIntegrationWizard } from "@modules/cdn/firebase"
import { MdWarning } from "react-icons/md"
import {
  StyledPanelBodySection,
  StyledPanelBoilerplate,
  useShowSuccessToast,
  Notification,
  Text,
  ThemeCss,
} from "gatsby-interface"
import {
  CdnIntegration,
  CdnVendor,
  HostingDomain,
} from "@modules/graphql/types"
import { CdnIntegrationSharedProps } from "../types"
import {
  ErrorAlert,
  useTriggerErrorAlert,
} from "@modules/ui/components/ErrorAlert"
import { cdnIntegration as cdnIntegrationText } from "@modules/locales/default.js"
import { cdnVendorNames } from "../constants"
import { interpolateMessage } from "@modules/locales"
import { useHostingDetailsForSiteQuery } from "@modules/cdn/gatsbyCloudCdn/queries.generated"
import { useConnectedCDN } from "@modules/site/shared/hooks/useConnectedCDN"
import { gatsbyCloudCdnIntegrationConfig as text } from "@modules/locales/default.js"
import Loading from "@modules/ui/components/Loading"

const domainsCss: ThemeCss = theme => ({
  listStyle: `none`,
  margin: 0,
  marginTop: theme.space[6],
  color: theme.colors.grey[60],
  fontWeight: theme.fontWeights.semiBold,
  fontSize: theme.fontSizes[1],

  li: {
    margin: `${theme.space[2]} 0`,
  },

  svg: {
    color: theme.colors.red[60],
    marginRight: theme.space[3],
    verticalAlign: `middle`,
  },
})

type GatsbyHostingWarningProps = {
  vendorName?: string
  domains?: HostingDomain[]
}

function GatsbyHostingWarning({
  vendorName,
  domains,
}: GatsbyHostingWarningProps) {
  return (
    <Notification
      tone="WARNING"
      variant="SECONDARY"
      contentAs="div"
      content={
        <div>
          <Text size="M">
            Are you sure you want to turn off <strong>Gatsby Hosting</strong>{" "}
            and switch to <strong>{vendorName}</strong>?
          </Text>

          <Text size="S">
            {text.messages.thisWillRemoveAndMakeDomainsUnavailable}
          </Text>
          {domains && (
            <ul css={domainsCss}>
              {domains.map(item => (
                <li key={item.id}>
                  <MdWarning /> {item.id}
                </li>
              ))}
            </ul>
          )}
        </div>
      }
      css={theme => ({
        marginBottom: theme.space[7],
        "& > div": {
          display: `block`,
          "& > svg": {
            float: `left`,
          },
        },
      })}
    />
  )
}

const integrationFormComponents: Record<
  CdnVendor,
  React.ComponentType<CdnIntegrationSharedProps> | null
> = {
  NETLIFY: NetlifyIntegrationWizard,
  FASTLY: FastlyIntegrationForm,
  FIREBASE: FirebaseIntegrationWizard,
  S3: AmazonS3IntegrationForm,
  GCS: GCloudStorageIntegrationForm,
  NOW: VercelIntegrationWizard,
  AZURE: AzureIntegrationForm,
  CLOUD_CDN: null,
}

const integrationTitles: Record<CdnVendor, string> = {
  AZURE: cdnIntegrationText.headers.azureIntegrationTitle,
  CLOUD_CDN: ``,
  GCS: cdnIntegrationText.headers.gcloudStorageIntegrationTitle,
  FASTLY: cdnIntegrationText.headers.fastlyIntegrationTitle,
  FIREBASE: cdnIntegrationText.headers.firebaseIntegrationTitle,
  NETLIFY: cdnIntegrationText.headers.netlifyIntegrationTitle,
  NOW: cdnIntegrationText.headers.vercelIntegrationTitle,
  S3: cdnIntegrationText.headers.amazonS3IntegrationTitle,
}

export type CdnIntegrationModalProps = {
  siteId: string
  siteName: string
  cdn: CdnIntegration
  onClose: () => void
  onSuccess: () => void
}

export function CdnIntegrationModal({
  siteId,
  siteName,
  cdn,
  onClose,
  onSuccess,
}: CdnIntegrationModalProps) {
  const [setError, errorAlert] = useTriggerErrorAlert()
  const showSuccessToast = useShowSuccessToast()

  const { vendor: connecedCdnVendor } = useConnectedCDN(siteId)

  const {
    data: hostingDetails,
    loading: hostingDetailsLoading,
  } = useHostingDetailsForSiteQuery({
    variables: { siteId },
  })

  const hostingDetailsForSite = hostingDetails?.hostingDetailsForSite?.[0]
  const domains = hostingDetailsForSite?.domains

  const IntegrationForm = integrationFormComponents[cdn.vendor]
  const vendorName = cdnVendorNames[cdn.vendor]

  return (
    <StyledPanelBoilerplate
      aria-label={interpolateMessage<"cdnVendorName">(
        cdnIntegrationText.headers.authorizeCdn,
        { cdnVendorName: vendorName }
      )}
      isOpen={Boolean(cdn)}
      onClose={onClose}
      header={integrationTitles[cdn.vendor]}
    >
      <StyledPanelBodySection>
        {hostingDetailsLoading && (
          <Loading
            variant="baby"
            delay={1000}
            css={theme => ({
              display: `flex`,
              marginBottom: theme.space[7],
            })}
          />
        )}

        {connecedCdnVendor === CdnVendor.CloudCdn && domains && (
          <GatsbyHostingWarning vendorName={vendorName} domains={domains} />
        )}

        {errorAlert}

        {IntegrationForm ? (
          <IntegrationForm
            siteId={siteId}
            siteName={siteName}
            integration={cdn}
            onCancel={onClose}
            onSetCDNConfig={({ success }) => {
              if (success) {
                onSuccess()
                onClose()
                return showSuccessToast(
                  cdnIntegrationText.messages.cdnIntegrationUpdated
                )
              }

              return setError(new Error(`Something went wrong`))
            }}
            onSubmitFailed={err => setError(err)}
          />
        ) : (
          <ErrorAlert>
            {interpolateMessage<"cdnVendorName">(
              cdnIntegrationText.messages.vendorNotSupported,
              { cdnVendorName: vendorName }
            )}
          </ErrorAlert>
        )}
      </StyledPanelBodySection>
    </StyledPanelBoilerplate>
  )
}
